import { useState } from "react";
import { RehabProtocolDto, rehabProtocolFormData } from "../types/rehabProtocol";
import { rehabProtocolService } from "../services/rehabProtocolService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const useRehabProtocols = () => {
  // 1. GET
  const {
    data: rehabProtocols = [],
    loading: isFetching,
    error: getError,
    refetch: fetchRehabProtocols,
    invalidateCache,
  } = useApi<RehabProtocolDto[]>("rehab-protocols", (signal) => rehabProtocolService.getAll({}, signal));

  // 2. CREATE
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newProtocol: typeof rehabProtocolFormData) => rehabProtocolService.create(newProtocol));

  // 3. UPDATE (Εδώ χρησιμοποιείς τα isUpdating και updateError)
  const {
    execute: updateExecution,
    loading: isUpdating,   // <-- Τώρα χρησιμοποιείται!
    error: updateError,    // <-- Τώρα χρησιμοποιείται!
  } = useMutation(({ id, data }: { id: number; data: typeof rehabProtocolFormData }) => 
    rehabProtocolService.update(id, data)
  );

  // 4. DELETE
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => rehabProtocolService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "success",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createRehabProtocol = async (newProtocol: typeof rehabProtocolFormData): Promise<void> => {
    try {
      await createExecution(newProtocol);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Το πρωτόκολλο αποκατάστασης αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      fetchRehabProtocols(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  // Συνάρτηση για το UPDATE (αξιοποιεί το updateExecution)
  const updateRehabProtocol = async (id: number, updatedData: typeof rehabProtocolFormData): Promise<void> => {
    try {
      await updateExecution({ id, data: updatedData });
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ενημέρωση ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      fetchRehabProtocols(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα ενημέρωσης";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const deleteRehabProtocols = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      fetchRehabProtocols(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    rehabProtocols,
    // Προσθέτουμε το isUpdating στη συνολική κατάσταση φόρτωσης
    isLoading: isFetching || isCreating || isUpdating || isDeleting,
    // Προσθέτουμε το updateError στα συνολικά σφάλματα
    error: getError || createError || updateError || deleteError,
    fetchRehabProtocols: () => fetchRehabProtocols(true),
    createRehabProtocol,
    updateRehabProtocol, // Επιστρέφουμε τη συνάρτηση update
    deleteRehabProtocols,
    modalState,
    closeModal,
  };
};